import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCMS } from "@/lib/CMSContext";
import { trackCTAClick, trackPlanSelected } from "@/hooks/usePageTracking";
import { SectionTracker } from "./SectionTracker";

const Pricing = () => {
  const { t } = useLanguage();
  const { cms } = useCMS();
  const plans = cms.pricing;

  return (
    <SectionTracker id="pricing">
    <section id="pricing" className="py-20 md:py-28 bg-secondary/50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-accent font-semibold text-sm uppercase tracking-wider">{t("pricing_label")}</span>
          <h2 className="font-display text-3xl md:text-4xl text-foreground mt-3 mb-4">
            {t("pricing_title")}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t("pricing_subtitle")}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.nameKey}
              initial={{ opacity: 0, y: 30, scale: plan.featured ? 0.95 : 1 }}
              whileInView={{ opacity: 1, y: 0, scale: plan.featured ? 1.03 : 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: i * 0.15, duration: 0.5 }}
              className={`rounded-xl p-8 relative ${
                plan.featured
                  ? "bg-primary text-primary-foreground shadow-[var(--shadow-card)] card-hover-lift"
                  : "bg-card text-foreground shadow-[var(--shadow-soft)] card-hover-lift"
              }`}
            >
              {plan.featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground text-xs font-bold px-4 py-1 rounded-full">
                  {t("pricing_mostPopular")}
                </span>
              )}
              <h3 className="font-display text-2xl mb-1">{t(plan.nameKey)}</h3>
              <p className={`text-sm mb-4 ${plan.featured ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                {t(plan.descKey)}
              </p>
              <div className="mb-6">
                <span className="font-display text-4xl">{plan.price}</span>
                <span className={`text-sm ${plan.featured ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                  {t("pricing_perMonth")}
                </span>
              </div>
              <ul className="space-y-3 mb-8">
                {plan.featureKeys.map((key) => (
                  <li key={key} className="flex items-start gap-2 text-sm">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${plan.featured ? "text-accent" : "text-primary"}`} />
                    <span>{t(key)}</span>
                  </li>
                ))}
              </ul>
              <Button
                variant={plan.featured ? "hero" : "heroPrimary"}
                className="w-full"
                size="lg"
                asChild
              >
                <Link
                  to={`/order?plan=${plan.nameKey}`}
                  onClick={() => {
                    trackCTAClick("pricing_get_started", { plan: plan.nameKey });
                    trackPlanSelected(plan.nameKey, plan.price);
                  }}
                >
                  {t("pricing_getStarted")}
                </Link>
              </Button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
    </SectionTracker>
  );
};

export default Pricing;
